// src/utils/rowHeadline.js
//
// The plain-language headline that leads the Raw requests detail pane (Slot Story
// step 4, AC 3.19-3.21, 3.35). It is the slot story's own verdict for the slot the
// selected row belongs to - never a second, independently worded answer - with two
// honesty rules:
//  - MITM row whose body was trimmed (`_bodyStripped`): if the story has no winner,
//    say "Winner not readable (response body no longer held)", never "no fill".
//  - USB/logcat row: when the verdict depends on the GAM response, append
//    "(USB capture: response not visible)".
// A row in no slot gets `<requestType> · <host>`.

import { buildSlotStory } from './slotStory';
import { slotRowKeys } from './slotHar';
import { billDuplicatesForLifecycle } from './mergeBillingPings';

export const STRIPPED_HEADLINE = 'Winner not readable (response body no longer held)';
export const USB_HEADLINE_SUFFIX = '(USB capture: response not visible)';

const keyOf = (r) => (r ? (r._k != null ? r._k : (r.requestId != null ? r.requestId : r.id)) : undefined);

/** Map rowKey -> lifecycle for every row a slot is made of (GAM row wins a tie). */
export function buildRowSlotIndex(lifecycles) {
  const idx = new Map();
  const list = Array.isArray(lifecycles) ? lifecycles : [];
  for (const lc of list) {
    try {
      for (const k of slotRowKeys(lc)) if (!idx.has(k)) idx.set(k, lc);
    } catch { /* malformed lifecycle: skip */ }
  }
  // a GAM request always names its own slot, even if another slot also lists it
  for (const lc of list) {
    const gk = lc && lc.phases && lc.phases.gam && lc.phases.gam.rowKey;
    if (gk != null) idx.set(gk, lc);
  }
  return idx;
}

function hostOfUrl(url) {
  const m = /^[a-z][a-z0-9+.-]*:\/\/([^/:?#]+)/i.exec(String(url || ''));
  return m ? m[1].toLowerCase() : '';
}

const isMitmStripped = (row) => !!(row && row._bodyStripped && row.captureSource !== 'logcat');

/**
 * buildRowHeadline(row, { slotIndex, rowsByKey, billGroups })
 *   -> { text, kind: 'slot'|'plain', slotKey, usb, stripped, notes[] }
 * `slotIndex` = buildRowSlotIndex(lifecycles). `billGroups` (optional) is the
 * mergeBillingPings() result: when the row is the representative of a merged
 * group its info issue is returned in `notes`. Never throws.
 */
export function buildRowHeadline(row, opts = {}) {
  const fallbackType = (row && (row.requestType || row.type)) || 'request';
  try {
    const key = keyOf(row);
    const lc = opts.slotIndex && key != null ? opts.slotIndex.get(key) : null;
    const group = opts.billGroups && opts.billGroups.byRep ? opts.billGroups.byRep.get(key) : null;
    const notes = group ? group.issues.slice() : [];

    if (!lc) {
      const host = (row && row.host) || hostOfUrl(row && row.url);
      return {
        text: host ? `${fallbackType} · ${host}` : String(fallbackType),
        kind: 'plain', slotKey: null, usb: !!(row && row.captureSource === 'logcat'), stripped: false, notes,
      };
    }

    const dups = opts.rowsByKey ? billDuplicatesForLifecycle(lc, opts.rowsByKey) : [];
    const story = buildSlotStory(lc, { duplicateBills: dups });
    const gamRow = lc.phases && lc.phases.gam && lc.phases.gam.row;
    const usbRow = !!((row && row.captureSource === 'logcat') || story.usb);
    const stripped = !usbRow && (isMitmStripped(row) || isMitmStripped(gamRow));

    let text = story.verdict;
    if (stripped && !story.winner) {
      text = STRIPPED_HEADLINE;
    } else if (usbRow && !(story.winner && story.winner.via === 'bill')) {
      text = `${text} ${USB_HEADLINE_SUFFIX}`;
    }
    return { text, kind: 'slot', slotKey: lc.key, usb: usbRow, stripped: stripped && !story.winner, notes };
  } catch {
    return { text: String(fallbackType), kind: 'plain', slotKey: null, usb: false, stripped: false, notes: [] };
  }
}
